import React, { useEffect, useState, useCallback } from "react";
import { X, PhoneOff, Voicemail, Phone, Check, AlertCircle } from "lucide-react";
import http from "@/lib/http";
import { t } from "@/i18n";

const OUTCOMES = [
  { key: "no_answer", labelKey: "call.no_answer", Icon: PhoneOff, num: 1 },
  { key: "voicemail", labelKey: "call.voicemail", Icon: Voicemail, num: 2 },
  { key: "answered", labelKey: "call.answered", Icon: Phone, num: 3 },
];

export default function QuickCallDialog({ lead, onClose, onSaved }) {
  const [outcome, setOutcome] = useState(null);
  const [note, setNote] = useState("");
  const [saving, setSaving] = useState(false);
  const [done, setDone] = useState(false);
  const [lockWarning, setLockWarning] = useState(null); // { locked_by_name }

  const contactName = lead.source_type === "makelaar"
    ? (lead.agent_name || "Makelaar")
    : (lead.owner_name || "Particulier");
  const contactPhone = lead.source_type === "makelaar" ? lead.agent_phone : lead.owner_phone;

  // Acquire soft-lock on mount
  useEffect(() => {
    let cancelled = false;
    http.post(`/api/leads/${lead.id}/lock`, { minutes: 5 })
      .then(r => {
        if (cancelled) return;
        if (!r.data.ok && r.data.locked_by_name) {
          setLockWarning({ name: r.data.locked_by_name });
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
      // Release lock when dialog closes (fire-and-forget)
      http.delete(`/api/leads/${lead.id}/lock`).catch(() => {});
    };
    // eslint-disable-next-line
  }, [lead.id]);

  const save = useCallback(async () => {
    if (!outcome) return;
    setSaving(true);
    try {
      await http.post(`/api/leads/${lead.id}/call`, { outcome, note: note || null });
      setDone(true);
      setTimeout(() => onSaved(), 900);
    } finally { setSaving(false); }
  }, [outcome, note, lead.id, onSaved]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") { e.preventDefault(); onClose(); return; }
      if (e.key === "1") { setOutcome("no_answer"); return; }
      if (e.key === "2") { setOutcome("voicemail"); return; }
      if (e.key === "3") { setOutcome("answered"); return; }
      if (e.key === "Enter" && outcome) { e.preventDefault(); save(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose, outcome, save]);

  return (
    <div className="fixed inset-0 z-50 bg-[rgba(27,36,48,0.45)] backdrop-blur-sm grid place-items-center p-4 fade-in"
         onClick={onClose}
         data-testid="quick-call-overlay">
      <div className="bg-white rounded-2xl w-full max-w-[480px] shadow-[0_20px_60px_-20px_rgba(14,90,94,0.4)] overflow-hidden"
           onClick={(e) => e.stopPropagation()}
           data-testid="quick-call-dialog">
        {done ? (
          <div className="p-10 text-center" data-testid="quick-call-success">
            <div className="w-12 h-12 mx-auto rounded-full bg-[#DFF3E7] text-[var(--success)] grid place-items-center mb-4">
              <Check size={22} />
            </div>
            <div className="font-display text-[20px] text-[var(--ink-900)]">{t("call.logged")}</div>
            <div className="text-[13px] text-[var(--ink-500)] mt-1">{t("call.logged_sub")}</div>
          </div>
        ) : (
          <>
            <div className="px-6 pt-5 pb-4 border-b border-[var(--border-soft)] flex items-start justify-between gap-4">
              <div>
                <div className="text-[11px] uppercase tracking-wider text-[var(--teal-700)] font-medium">
                  {t("call.title")}
                </div>
                <h3 className="font-display text-[22px] leading-tight text-[var(--ink-900)] mt-1">{contactName}</h3>
                {contactPhone && (
                  <a href={`tel:${contactPhone}`} className="text-[13px] text-[var(--teal-600)] hover:underline">
                    {contactPhone}
                  </a>
                )}
                <div className="text-[12px] text-[var(--ink-500)] mt-1">
                  {lead.address}, {lead.postal_code} {lead.city}
                </div>
              </div>
              <button type="button" onClick={onClose} data-testid="quick-call-close"
                      className="p-2 rounded-lg hover:bg-[var(--sand-100)] text-[var(--ink-500)]">
                <X size={16} />
              </button>
            </div>

            {lockWarning && (
              <div className="mx-6 mt-4 p-3 rounded-lg bg-[#FDF3DF] border border-[#EAD49B] flex items-start gap-3"
                   data-testid="call-lock-warning">
                <AlertCircle size={16} className="text-[#8A5D10] mt-0.5 shrink-0" />
                <div className="text-[12px] text-[#5A3F0B]">
                  <div className="font-semibold">{t("call.lock_warning", { name: lockWarning.name })}</div>
                  <div className="mt-0.5">{t("call.lock_warning_sub")}</div>
                </div>
              </div>
            )}

            <div className="px-6 py-5">
              <div className="text-[13px] font-medium text-[var(--ink-700)] mb-3">{t("call.what_outcome")}</div>
              <div className="grid grid-cols-3 gap-2">
                {OUTCOMES.map(o => {
                  const selected = outcome === o.key;
                  return (
                    <button key={o.key} type="button"
                            data-testid={`call-outcome-${o.key}`}
                            onClick={() => setOutcome(o.key)}
                            className={`flex flex-col items-center gap-2 p-3 rounded-xl border text-[13px] font-medium transition-all
                              ${selected
                                ? "border-[var(--teal-500)] bg-[var(--teal-50)] text-[var(--teal-700)]"
                                : "border-[var(--border-soft)] hover:border-[var(--border-strong)] text-[var(--ink-700)]"}`}>
                      <div className={`w-9 h-9 rounded-lg grid place-items-center
                        ${selected ? "bg-[var(--teal-600)] text-white" : "bg-[var(--sand-100)] text-[var(--ink-500)]"}`}>
                        <o.Icon size={16} />
                      </div>
                      <span>{t(o.labelKey)}</span>
                      <span className="text-[10px] text-[var(--ink-400)] font-mono">{o.num}</span>
                    </button>
                  );
                })}
              </div>

              <label className="block mt-5">
                <span className="text-[13px] font-medium text-[var(--ink-700)]">{t("call.note_optional")}</span>
                <textarea data-testid="call-note-input" rows={3} value={note}
                          onChange={(e) => setNote(e.target.value)}
                          className="mt-1 w-full px-3 py-2 rounded-lg border border-[var(--border-soft)] text-sm outline-none focus:border-[var(--teal-500)] resize-none" />
              </label>

              <div className="text-[11px] text-[var(--ink-400)] mt-3 font-mono">
                {t("call.save_shortcut")}
              </div>
            </div>

            <div className="px-6 py-4 bg-[var(--sand-50)] border-t border-[var(--border-soft)] flex justify-end gap-2">
              <button type="button" onClick={onClose} className="btn btn-ghost">{t("common.cancel")}</button>
              <button type="button" onClick={save} disabled={!outcome || saving}
                      data-testid="call-save-btn"
                      className="btn btn-primary disabled:opacity-50">
                {saving ? t("common.loading") : t("common.save")}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
